import type React from 'react';
import { useEffect, useReducer, createContext, useContext } from 'react';

interface ModalContextValues {
  component: React.FC
  modalProps: any
  showModal: (component: React.ComponentType<any>, modalProps?: any) => void
  hideModal: () => void
}

const ModalContext = createContext<ModalContextValues>({
  component: () => (
    <div>Modal?</div>
  ),
  modalProps: {},
  showModal: () => undefined,
  hideModal: () => undefined
});

const { Provider, Consumer: ModalConsumer } = ModalContext;

const ACTION_MODAL_OPEN = 'modalOpen';
const ACTION_MODAL_CLOSE = 'modalClose';

const reducer = (
  state: any,
  {
    type,
    component,
    modalProps
  }: { type: string, component?: React.FC, modalProps?: any }
): any => {
  switch (type) {
    case ACTION_MODAL_OPEN:
      return {
        ...state,
        component,
        modalProps
      };
    case ACTION_MODAL_CLOSE:
      return {
        ...state,
        component: null,
        modalProps: {}
      };
  }

  return state;
}

export interface ModalProviderProps {
  children: React.ReactNode
}

const ModalComponent = (): any => {
  const { component: Component, modalProps, hideModal, showModal } = useModal();
  return Component ? (
    <Component
      {...modalProps}
      hideModal={hideModal}
      showModal={showModal}
    />
  ) : null;
}

const ModalProvider = ({ children }: ModalProviderProps): any => {
  const initialState = {
    component: null,
    modalProps: {},
    showModal: (component: any, modalProps = {}) => {
      dispatch({
        type: ACTION_MODAL_OPEN,
        component,
        modalProps
      });
    },
    hideModal: () => {
      dispatch({
        type: ACTION_MODAL_CLOSE
      });
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const onKeyDown = (e: any): void => {
    if (e.key === 'Escape') {
      state.hideModal();
    }
  };

  useEffect(() => {
    if (state.component === null) {
      document.addEventListener('keydown', onKeyDown);
    } else {
      document.removeEventListener('keydown', onKeyDown);
    }
  }, [state.component]);

  return (
    <div onKeyDown={onKeyDown} className="app-modal-wrapper">
      <Provider value={state}>
        <ModalComponent />
        {children}
      </Provider>
    </div>
  );
}

const useModal = (): ModalContextValues => useContext(ModalContext);

export {
  ModalConsumer,
  ModalProvider,
  useModal
};
